import React, { useState, useEffect } from 'react';
import { invoiceAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function InvoiceManagement() {
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // State
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalInvoices: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    restaurantId: '',
    status: '',
    financialYear: '',
    sortBy: 'invoiceDate',
    sortOrder: 'desc'
  });

  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [showTDSModal, setShowTDSModal] = useState(false);
  const [tdsMonth, setTdsMonth] = useState('');
  const [tdsYear, setTdsYear] = useState('');

  // Colors
  const primaryColor = '#FF6961';
  const primaryDark = '#E6524A';
  const textPrimary = '#0F172A';
  const textSecondary = '#64748B';
  const backgroundColor = '#F8FAFC';

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [activeTab, filters, pagination.page]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      // Add invoice type filter
      if (activeTab !== 'all') {
        if (activeTab === 'customer') {
          params.invoiceType = 'customer_food,customer_platform_fee';
        } else if (activeTab === 'restaurant') {
          params.invoiceType = 'platform_service_fee,advertising_marketing,subscription';
        } else if (activeTab === 'tds') {
          params.invoiceType = 'tds_statement';
        } else if (activeTab === 'settlement') {
          params.invoiceType = 'settlement_statement';
        } else if (activeTab === 'delivery_partner') {
          params.invoiceType = 'dp_earnings_statement,dp_tds_statement,dp_payout_statement';
        }
      }

      // Add other filters
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.restaurantId) params.restaurantId = filters.restaurantId;
      if (filters.status) params.status = filters.status;
      if (filters.financialYear) params.financialYear = filters.financialYear;

      const response = await invoiceAPI.getAll(params);
      // Backend getAllInvoices returns raw lean() Mongo docs with `_id`
      // (not `invoiceId`). The user-facing getUserInvoices endpoint maps
      // _id → invoiceId, so this page historically assumed the admin list
      // did too — which it doesn't. Normalize here so every row has an
      // `invoiceId` the action handlers can trust. Without this the
      // regenerate POST was going to /admin/undefined/regenerate-pdf.
      const rawInvoices = response.data.data.invoices || [];
      setInvoices(
        rawInvoices.map((inv) => ({
          ...inv,
          invoiceId: inv.invoiceId || inv._id,
        }))
      );
      setPagination({
        ...pagination,
        totalPages: response.data.data.pagination?.totalPages || 1,
        totalInvoices: response.data.data.pagination?.totalInvoices || 0,
        page: response.data.data.pagination?.page || 1
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.financialYear) params.financialYear = filters.financialYear;

      const response = await invoiceAPI.getStats(params);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewDetails = async (invoice) => {
    setLoading(true);
    try {
      const response = await invoiceAPI.getById(invoice.invoiceId);
      setSelectedInvoice(response.data.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setError('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };

  const handleRegeneratePDF = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to regenerate this invoice PDF?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await invoiceAPI.regeneratePDF(invoiceId);
      setSuccess('Invoice PDF regenerated successfully');
      fetchInvoices();
    } catch (error) {
      console.error('Error regenerating PDF:', error);
      setError(error.response?.data?.message || 'Failed to regenerate PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailInvoice = async () => {
    if (!emailAddress || !selectedInvoice) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await invoiceAPI.emailInvoice(selectedInvoice.invoiceId, emailAddress);
      setSuccess(`Invoice emailed successfully to ${emailAddress}`);
      setShowEmailModal(false);
      setEmailAddress('');
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error emailing invoice:', error);
      setError(error.response?.data?.message || 'Failed to email invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateMonthlyTDS = async () => {
    if (!tdsMonth || !tdsYear) {
      setError('Please select month and year');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await invoiceAPI.generateMonthlyTDS(parseInt(tdsMonth), parseInt(tdsYear));
      setSuccess('Monthly TDS statements generated successfully');
      setShowTDSModal(false);
      setTdsMonth('');
      setTdsYear('');
      fetchInvoices();
    } catch (error) {
      console.error('Error generating TDS:', error);
      setError(error.response?.data?.message || 'Failed to generate TDS statements');
    } finally {
      setLoading(false);
    }
  };

  const formatInvoiceType = (type) => {
    const types = {
      customer_food: 'Food Invoice',
      customer_platform_fee: 'Platform Fee Invoice',
      platform_service_fee: 'Platform Fee',
      advertising_marketing: 'Advertising',
      subscription: 'Subscription',
      tds_statement: 'TDS Statement',
      credit_note: 'Credit Note',
      debit_note: 'Debit Note',
      settlement_statement: 'Settlement',
      dp_earnings_statement: 'DP Earnings',
      dp_tds_statement: 'DP TDS',
      dp_payout_statement: 'DP Payout'
    };
    return types[type] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#10B981';
      case 'pending':
        return '#F59E0B';
      case 'cancelled':
        return '#EF4444';
      default:
        return textSecondary;
    }
  };

  const getTypeColor = (type) => {
    if (['customer_food', 'customer_platform_fee'].includes(type)) {
      return '#3B82F6';
    } else if (['platform_service_fee', 'advertising_marketing', 'subscription'].includes(type)) {
      return primaryColor;
    } else if (type === 'tds_statement') {
      return '#8B5CF6';
    } else if (type === 'settlement_statement') {
      return '#10B981';
    } else if (['credit_note', 'debit_note'].includes(type)) {
      return '#F59E0B';
    } else if (type === 'dp_earnings_statement') {
      return '#059669'; // Emerald green
    } else if (type === 'dp_tds_statement') {
      return '#7C3AED'; // Purple
    } else if (type === 'dp_payout_statement') {
      return '#0891B2'; // Cyan
    }
    return textSecondary;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination({ ...pagination, page: newPage });
    }
  };

  const openEmailModal = (invoice) => {
    setSelectedInvoice(invoice);
    setEmailAddress(invoice.recipientEmail || '');
    setShowEmailModal(true);
  };

  return (
    <>
      <Navbar />
      <div style={{ padding: '24px', backgroundColor, minHeight: '100vh' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ marginBottom: '8px', color: textPrimary, fontSize: '28px', fontWeight: '700' }}>
            📄 Invoice Management
          </h1>
          <p style={{ color: textSecondary, fontSize: '14px' }}>
            Manage all invoices, TDS statements, and settlements
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid #EF4444',
            borderRadius: '8px',
            color: '#EF4444'
          }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '2px solid #10B981',
            borderRadius: '8px',
            color: '#10B981'
          }}>
            {success}
          </div>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ color: textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                Total Invoices
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: textPrimary }}>
                {stats.totalInvoices || 0}
              </div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ color: textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                Total Amount
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: primaryColor }}>
                {formatCurrency(stats.totalAmount || 0)}
              </div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ color: textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                Tax Collected
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#8B5CF6' }}>
                {formatCurrency(stats.totalTax || 0)}
              </div>
            </div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ color: textSecondary, fontSize: '14px', marginBottom: '8px' }}>
                TDS Deducted
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#F59E0B' }}>
                {formatCurrency(stats.totalTDS || 0)}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          borderBottom: '2px solid #E5E7EB',
          flexWrap: 'wrap'
        }}>
          {['all', 'customer', 'restaurant', 'delivery_partner', 'tds', 'settlement'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setPagination({ ...pagination, page: 1 });
              }}
              style={{
                padding: '12px 24px',
                background: activeTab === tab ? primaryColor : 'transparent',
                color: activeTab === tab ? 'white' : textSecondary,
                border: 'none',
                borderBottom: activeTab === tab ? `3px solid ${primaryDark}` : 'none',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'all' ? '📊 All' :
               tab === 'customer' ? '🍔 Customer' :
               tab === 'restaurant' ? '🏪 Restaurant' :
               tab === 'delivery_partner' ? '🛵 Delivery Partner' :
               tab === 'tds' ? '📋 TDS' :
               '💰 Settlement'}
            </button>
          ))}
        </div>

        {/* Filters Panel */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: textPrimary }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: textPrimary }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: textPrimary }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB',
                  fontSize: '14px'
                }}
              >
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: textPrimary }}>
                Financial Year
              </label>
              <select
                value={filters.financialYear}
                onChange={(e) => setFilters({ ...filters, financialYear: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB',
                  fontSize: '14px'
                }}
              >
                <option value="">All Years</option>
                <option value="2024-2025">FY 2024-25</option>
                <option value="2025-2026">FY 2025-26</option>
                <option value="2026-2027">FY 2026-27</option>
              </select>
            </div>
            <div>
              <button
                onClick={() => {
                  fetchInvoices();
                  fetchStats();
                }}
                style={{
                  width: '100%',
                  padding: '10px 20px',
                  background: primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = primaryDark}
                onMouseLeave={(e) => e.target.style.background = primaryColor}
              >
                🔍 Apply Filters
              </button>
            </div>
            <div>
              <button
                onClick={() => setShowTDSModal(true)}
                style={{
                  width: '100%',
                  padding: '10px 20px',
                  background: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#7C3AED'}
                onMouseLeave={(e) => e.target.style.background = '#8B5CF6'}
              >
                📋 Generate TDS
              </button>
            </div>
          </div>
        </div>

        {/* Invoice Table */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '48px',
            background: 'white',
            borderRadius: '12px',
            color: textSecondary
          }}>
            Loading invoices...
          </div>
        ) : invoices.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px',
            background: 'white',
            borderRadius: '12px',
            color: textSecondary
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No invoices found</p>
            <p style={{ fontSize: '14px' }}>Try adjusting your filters</p>
          </div>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: textPrimary }}>
                      Invoice #
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: textPrimary }}>
                      Type
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: textPrimary }}>
                      Recipient
                    </th>
                    <th style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: textPrimary }}>
                      Amount
                    </th>
                    <th style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: textPrimary }}>
                      Tax
                    </th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: textPrimary }}>
                      Date
                    </th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: textPrimary }}>
                      Status
                    </th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: textPrimary }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.invoiceId} style={{ borderBottom: '1px solid #E5E7EB' }}>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '600', color: textPrimary }}>
                          {invoice.invoiceNumber}
                        </div>
                        <div style={{ fontSize: '12px', color: textSecondary }}>
                          {invoice.invoiceId?.slice(0, 8)}...
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: `${getTypeColor(invoice.invoiceType)}20`,
                          color: getTypeColor(invoice.invoiceType)
                        }}>
                          {formatInvoiceType(invoice.invoiceType)}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontWeight: '500', color: textPrimary }}>
                          {invoice.recipientName || 'N/A'}
                        </div>
                        {invoice.recipientEmail && (
                          <div style={{ fontSize: '12px', color: textSecondary }}>
                            {invoice.recipientEmail}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: textPrimary }}>
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'right', color: textSecondary }}>
                        {formatCurrency(invoice.taxAmount || 0)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center', color: textSecondary }}>
                        {formatDate(invoice.invoiceDate)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: `${getStatusColor(invoice.status)}20`,
                          color: getStatusColor(invoice.status),
                          textTransform: 'capitalize'
                        }}>
                          {invoice.status}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleViewDetails(invoice)}
                            title="View Details"
                            style={{
                              padding: '6px 12px',
                              background: '#3B82F6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            👁️ View
                          </button>
                          {invoice.pdfUrl && (
                            <a
                              href={invoice.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '6px 12px',
                                background: '#10B981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                textDecoration: 'none',
                                fontWeight: '600'
                              }}
                            >
                              📥 PDF
                            </a>
                          )}
                          <button
                            onClick={() => handleRegeneratePDF(invoice.invoiceId)}
                            title="Regenerate PDF"
                            style={{
                              padding: '6px 12px',
                              background: '#F59E0B',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            🔄
                          </button>
                          <button
                            onClick={() => openEmailModal(invoice)}
                            title="Email Invoice"
                            style={{
                              padding: '6px 12px',
                              background: '#8B5CF6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            📧
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{
              padding: '16px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ color: textSecondary, fontSize: '14px' }}>
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.totalInvoices)} of{' '}
                {pagination.totalInvoices} invoices
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '8px 16px',
                    background: pagination.page === 1 ? '#E5E7EB' : primaryColor,
                    color: pagination.page === 1 ? textSecondary : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Previous
                </button>
                <div style={{
                  padding: '8px 16px',
                  background: '#F9FAFB',
                  borderRadius: '6px',
                  fontWeight: '600',
                  color: textPrimary
                }}>
                  Page {pagination.page} of {pagination.totalPages}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  style={{
                    padding: '8px 16px',
                    background: pagination.page === pagination.totalPages ? '#E5E7EB' : primaryColor,
                    color: pagination.page === pagination.totalPages ? textSecondary : 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Details Modal */}
      {showDetailsModal && selectedInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 1
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: textPrimary, margin: 0 }}>
                Invoice Details
              </h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedInvoice(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: textSecondary
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: textSecondary, marginBottom: '4px' }}>
                      Invoice Number
                    </div>
                    <div style={{ fontWeight: '600', color: textPrimary }}>
                      {selectedInvoice.invoiceNumber}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: textSecondary, marginBottom: '4px' }}>
                      Invoice Date
                    </div>
                    <div style={{ fontWeight: '600', color: textPrimary }}>
                      {formatDate(selectedInvoice.invoiceDate)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: textSecondary, marginBottom: '4px' }}>
                      Type
                    </div>
                    <div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: `${getTypeColor(selectedInvoice.invoiceType)}20`,
                        color: getTypeColor(selectedInvoice.invoiceType)
                      }}>
                        {formatInvoiceType(selectedInvoice.invoiceType)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: textSecondary, marginBottom: '4px' }}>
                      Status
                    </div>
                    <div>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: `${getStatusColor(selectedInvoice.status)}20`,
                        color: getStatusColor(selectedInvoice.status),
                        textTransform: 'capitalize'
                      }}>
                        {selectedInvoice.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                borderTop: '1px solid #E5E7EB',
                paddingTop: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: textPrimary, marginBottom: '12px' }}>
                  Recipient Details
                </h3>
                <div style={{ fontSize: '14px', color: textPrimary, lineHeight: '1.6' }}>
                  <div><strong>Name:</strong> {selectedInvoice.recipientName || 'N/A'}</div>
                  {selectedInvoice.recipientEmail && (
                    <div><strong>Email:</strong> {selectedInvoice.recipientEmail}</div>
                  )}
                  {selectedInvoice.recipientAddress && (
                    <div><strong>Address:</strong> {selectedInvoice.recipientAddress}</div>
                  )}
                  {selectedInvoice.recipientGSTIN && (
                    <div><strong>GSTIN:</strong> {selectedInvoice.recipientGSTIN}</div>
                  )}
                </div>
              </div>

              <div style={{
                borderTop: '1px solid #E5E7EB',
                paddingTop: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: textPrimary, marginBottom: '12px' }}>
                  Amount Details
                </h3>
                <div style={{ fontSize: '14px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    color: textPrimary
                  }}>
                    <span>Subtotal:</span>
                    <span style={{ fontWeight: '600' }}>
                      {formatCurrency(selectedInvoice.subtotal)}
                    </span>
                  </div>
                  {selectedInvoice.taxAmount > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      color: textPrimary
                    }}>
                      <span>Tax ({selectedInvoice.taxRate}%):</span>
                      <span style={{ fontWeight: '600' }}>
                        {formatCurrency(selectedInvoice.taxAmount)}
                      </span>
                    </div>
                  )}
                  {selectedInvoice.tdsAmount > 0 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      color: '#F59E0B'
                    }}>
                      <span>TDS Deducted ({selectedInvoice.tdsRate}%):</span>
                      <span style={{ fontWeight: '600' }}>
                        -{formatCurrency(selectedInvoice.tdsAmount)}
                      </span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '12px',
                    borderTop: '2px solid #E5E7EB',
                    fontSize: '18px',
                    color: primaryColor,
                    fontWeight: '700'
                  }}>
                    <span>Total Amount:</span>
                    <span>{formatCurrency(selectedInvoice.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {selectedInvoice.lineItems && selectedInvoice.lineItems.length > 0 && (
                <div style={{
                  borderTop: '1px solid #E5E7EB',
                  paddingTop: '16px'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: textPrimary, marginBottom: '12px' }}>
                    Line Items
                  </h3>
                  <div style={{ fontSize: '14px' }}>
                    {selectedInvoice.lineItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px',
                          background: index % 2 === 0 ? '#F9FAFB' : 'white',
                          borderRadius: '6px',
                          marginBottom: '4px'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '600', color: textPrimary }}>
                            {item.description}
                          </div>
                          {item.quantity && (
                            <div style={{ fontSize: '12px', color: textSecondary }}>
                              Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                            </div>
                          )}
                        </div>
                        <div style={{ fontWeight: '600', color: textPrimary }}>
                          {formatCurrency(item.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Invoice Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: textPrimary, margin: 0 }}>
                Email Invoice
              </h2>
            </div>
            <div style={{ padding: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: textPrimary
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="recipient@example.com"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setEmailAddress('');
                    setSelectedInvoice(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#E5E7EB',
                    color: textPrimary,
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEmailInvoice}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    background: primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate TDS Modal */}
      {showTDSModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: textPrimary, margin: 0 }}>
                Generate Monthly TDS
              </h2>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: textPrimary
                }}>
                  Month
                </label>
                <select
                  value={tdsMonth}
                  onChange={(e) => setTdsMonth(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #E5E7EB',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Month</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2024, i).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: textPrimary
                }}>
                  Year
                </label>
                <select
                  value={tdsYear}
                  onChange={(e) => setTdsYear(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #E5E7EB',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select Year</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                  <option value="2027">2027</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowTDSModal(false);
                    setTdsMonth('');
                    setTdsYear('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#E5E7EB',
                    color: textPrimary,
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateMonthlyTDS}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    background: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InvoiceManagement;
